import { getDataKeyName } from "../../../../utilities/UnitsAndKeys/getDataKeyName";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { getChannelColors } from "../LiveMeterGraph.utils";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import React, { useEffect, useRef } from "react";
import { useTheme } from "@material-ui/core";
import { getValueUnitDivisor } from "../../../../utilities/UnitsAndKeys/convertUnit";
import getDataType from "../../../../utilities/UnitsAndKeys/getDataType";
import { styleLegend } from "../../../../utilities/Charts/styles";
am4core.useTheme(am4themes_animated);

export const ChannelsGraph = ({
  data,
  seriesNames,
  selectedMeterIndex,
  duration = 30,
  height = 400,
  dataKey = "realPower",
}) => {
  const theme = useTheme();
  const { unit } = getValueUnitDivisor(1, getDataType(dataKey));
  const channelColors = useRef(getChannelColors());
  const chart = useRef(null);
  const grey =
    (theme.palette.colors &&
      theme.palette.colors.grey &&
      theme.palette.colors.grey.dark) ||
    "#808080";

  // Init graph
  useEffect(() => {
    chart.current = am4core.create("channelsgraph", am4charts.XYChart);
    return () => chart.current && chart.current.dispose();
  }, [dataKey]);

  // Add data
  useEffect(() => {
    if (
      data &&
      chart.current.data.length &&
      chart.current.data[chart.current.data.length - 1].time.getTime() <
        data[0].time.getTime()
    ) {
      chart.current && chart.current.addData(data);
    }
  }, [data]);

  // reset data on meter change
  useEffect(() => {
    if (chart.current && chart.current.data) {
      chart.current.dispose();
      chart.current = am4core.create("channelsgraph", am4charts.XYChart);
    }
  }, [selectedMeterIndex]);

  // Style graph
  useEffect(() => {
    if (chart.current && data && !chart.current.data.length) {
      const zoomButtonBG = chart.current.zoomOutButton.background;
      zoomButtonBG.fill = am4core.color(theme.palette.secondary.main);
      if (zoomButtonBG.states.getKey("hover").properties.fill) {
        zoomButtonBG.states.getKey("hover").properties.fill = am4core.color(
          theme.palette.secondary.dark
        );
      }
      if (zoomButtonBG.states.getKey("down").properties.fill) {
        zoomButtonBG.states.getKey("down").properties.fill = am4core.color(
          theme.palette.secondary.main
        );
      }

      chart.current.data = data;

      chart.current.numberFormatter.numberFormat =
        dataKey === "powerFactor" ? "#.####" : "#.##";
      chart.current.paddingRight = 35;

      // Axes =====
      let dateAxis = chart.current.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.grid.template.location = 0;
      dateAxis.baseInterval = {
        timeUnit: "millisecond",
        count: duration,
      };
      dateAxis.title.text = "[bold]Time[/]";
      dateAxis.title.fontSize = theme.typography.body1.fontSize;
      dateAxis.tooltipDateFormat = "HH:mm:ss";
      if (dateAxis.tooltip) {
        dateAxis.tooltip.getFillFromObject = false;
        dateAxis.tooltip.getStrokeFromObject = false;
        dateAxis.tooltip.background.fill = am4core.color(
          theme.palette.secondary.main
        );
        dateAxis.tooltip.background.stroke = am4core.color(
          theme.palette.secondary.main
        );
      }
      dateAxis.dateFormats.setKey("second", "HH:mm:ss");
      dateAxis.periodChangeDateFormats.setKey("second", "HH:mm:ss");
      dateAxis.fill = am4core.color(grey);
      dateAxis.title.fill = am4core.color(grey);
      dateAxis.renderer.labels.template.fill = am4core.color(grey);

      let valueAxis = chart.current.yAxes.push(new am4charts.ValueAxis());
      valueAxis.title.text = `[bold]${getDataKeyName(dataKey)} (${unit})[/]`;
      if (valueAxis.tooltip) {
        valueAxis.tooltip.getFillFromObject = false;
        valueAxis.tooltip.getStrokeFromObject = false;
        valueAxis.tooltip.background.fill = am4core.color(
          theme.palette.secondary.main
        );
        valueAxis.tooltip.background.stroke = am4core.color(
          theme.palette.secondary.main
        );
      }
      valueAxis.title.fontSize = theme.typography.body1.fontSize;
      valueAxis.title.fill = am4core.color(grey);
      valueAxis.renderer.labels.template.fill = am4core.color(grey);

      // Series =====
      let currentColorIndex = 0;
      seriesNames.forEach((name) => {
        if (chart.current) {
          const series = chart.current.series.push(new am4charts.LineSeries());

          // bullets
          const bullet = series.bullets.push(new am4charts.Bullet());
          let point = bullet.createChild(am4core.Circle);
          point.width = 3;
          point.height = 3;
          point.horizontalCenter = "middle";
          point.verticalCenter = "middle";

          series.name = name;
          series.dataFields.dateX = "time";
          series.dataFields.valueY = `${name}:${dataKey}`;
          series.tooltipHTML = `${name}: <strong>{valueY.value} ${unit}</strong>`;
          series.strokeWidth = 2;

          // handle colors
          if (name === "Grid") {
            series.stroke = am4core.color("#808080");
            series.fill = am4core.color("#808080");
          } else if (name === "Solar") {
            series.stroke = am4core.color("#FFB50A");
            series.fill = am4core.color("#FFB50A");
          } else {
            series.stroke = am4core.color(
              channelColors.current[currentColorIndex]
            );
            series.fill = am4core.color(
              channelColors.current[currentColorIndex]
            );
            currentColorIndex++;
          }
        }
      });

      chart.current.legend = new am4charts.Legend();
      styleLegend(chart.current.legend);

      chart.current.cursor = new am4charts.XYCursor();
    }
  }, [theme, dataKey, channelColors, data]);

  return <div style={{ height: height }} id={"channelsgraph"} />;
};

export default ChannelsGraph;



// WEBPACK FOOTER //
// ./resources/assets/js/app/SchoolProfileView/components/LiveMeterGraph/ChannelsGraph/ChannelsGraph.js